'use client';
import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import CoachKpiCards from './components/CoachKpiCards';
import AthleteRosterTable from './components/AthleteRosterTable';
import CoachWellnessPanel from './components/CoachWellnessPanel';
import AddAthleteModal from './components/AddAthleteModal';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { generateInterpretation } from '@/lib/scoring';
import { getAthleteFlag, getWeakestCategory } from './components/rosterUtils';
import { Users, UserPlus } from 'lucide-react';

interface RosterEntry {
  athlete: {
    id: string;
    name: string;
    sport: string;
    position: string;
    seasonPhase: string;
    avatarInitials: string;
    email: string;
    inviteStatus: string;
  };
  session: {
    id: string;
    date: string;
    eps: number;
    engineScore: number;
    ignitionScore: number;
    bounceScore: number;
  } | null;
  profile: ReturnType<typeof generateInterpretation> | null;
  flag: 'green' | 'yellow' | 'red';
  weakestCategory: string;
  trend: 'up' | 'neutral' | 'down';
}

export default function CoachDashboardPage() {
  const { user } = useAuth();
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');
  const [userRole, setUserRole] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);

  const canAddAthlete = userRole === 'coach' || userRole === 'admin';

  const loadRoster = useCallback(async () => {
    if (!user) return;
    const supabase = createClient();

    setLoading(true);

    // Get athletes linked to this coach
    const { data: coachAthletes } = await supabase
      .from('coach_athletes')
      .select('athlete_id, invite_status')
      .eq('coach_id', user.id)
      .eq('status', 'active');

    const athleteIds = (coachAthletes ?? []).map((r: any) => r.athlete_id);
    const inviteStatusMap: Record<string, string> = {};
    for (const ca of coachAthletes ?? []) {
      inviteStatusMap[ca.athlete_id] = ca.invite_status ?? 'not_invited';
    }

    if (athleteIds.length === 0) {
      setRoster([]);
      setLoading(false);
      return;
    }

    // Load athlete profiles
    const { data: profiles } = await supabase
      .from('athlete_profiles')
      .select('id, name, sport, position, season_phase, avatar_initials, email')
      .in('id', athleteIds);

    // Load latest test session per athlete
    const { data: sessions } = await supabase
      .from('test_sessions')
      .select('id, athlete_id, session_date, eps, engine_score, ignition_score, bounce_score')
      .in('athlete_id', athleteIds)
      .order('session_date', { ascending: false });

    const latestByAthlete: Record<string, any> = {};
    for (const s of sessions ?? []) {
      if (!latestByAthlete[s.athlete_id]) {
        latestByAthlete[s.athlete_id] = s;
      }
    }

    const entries: RosterEntry[] = (profiles ?? []).map((ap: any) => {
      const s = latestByAthlete[ap.id] ?? null;
      const sess = s ? {
        id: s.id,
        date: s.session_date,
        eps: Number(s.eps),
        engineScore: Number(s.engine_score),
        ignitionScore: Number(s.ignition_score),
        bounceScore: Number(s.bounce_score),
      } : null;
      const profile = sess ? generateInterpretation(sess.engineScore, sess.ignitionScore, sess.bounceScore) : null;
      return {
        athlete: {
          id: ap.id,
          name: ap.name ?? '',
          sport: ap.sport ?? '',
          position: ap.position ?? '',
          seasonPhase: ap.season_phase ?? '',
          avatarInitials: ap.avatar_initials ?? (ap.name ?? '??').slice(0, 2).toUpperCase(),
          email: ap.email ?? '',
          inviteStatus: inviteStatusMap[ap.id] ?? 'not_invited',
        },
        session: sess,
        profile,
        flag: sess ? getAthleteFlag(sess.eps, sess.engineScore, sess.ignitionScore, sess.bounceScore) : 'red',
        weakestCategory: sess ? getWeakestCategory(sess.engineScore, sess.ignitionScore, sess.bounceScore) : 'N/A',
        trend: sess ? (sess.eps >= 75 ? 'up' : sess.eps >= 65 ? 'neutral' : 'down') : 'neutral',
      };
    });

    setRoster(entries);
    setLastUpdated(new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    // Fetch user role — user_profiles does not exist; use auth metadata + coach_profiles
    const fetchRole = async () => {
      const supabase = createClient();

      // Primary: check auth metadata
      const metaRole = user.user_metadata?.role ?? '';
      if (metaRole) {
        setUserRole(metaRole);
        return;
      }

      // Fallback: check if a coach_profiles record exists for this user
      const { data: coachData } = await supabase
        .from('coach_profiles')
        .select('user_id')
        .eq('user_id', user.id)
        .single();

      if (coachData) {
        setUserRole('coach');
      } else {
        setUserRole('athlete');
      }
    };

    fetchRole();
    loadRoster();
  }, [user, loadRoster]);

  const handleAthleteAdded = useCallback(() => {
    loadRoster();
  }, [loadRoster]);

  const assessed = roster.filter(r => r.session);
  const avgEps = assessed.length > 0
    ? Math.round(assessed.reduce((sum, r) => sum + (r.session?.eps ?? 0), 0) / assessed.length)
    : 0;
  const belowThreshold = assessed.filter(r => (r.session?.eps ?? 0) < 65).length;
  const mostImproved = assessed.length > 0
    ? assessed.reduce((best, r) => (r.session?.eps ?? 0) > (best.session?.eps ?? 0) ? r : best, assessed[0])?.athlete?.name ?? '—' :'—';
  const sessionsThisMonth = 0;

  if (loading) {
    return (
      <AppLayout activePath="/coach-dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-3">
            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-zinc-500">Loading coach dashboard...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout activePath="/coach-dashboard">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="page-title">Coach Dashboard</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Squad overview · {roster.length} athlete{roster.length !== 1 ? 's' : ''}
              {lastUpdated ? ` · Last updated ${lastUpdated}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {canAddAthlete && (
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #E10600, #8B0000)',
                  color: '#ffffff',
                  boxShadow: '0 0 16px rgba(225,6,0,0.3)',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 0 24px rgba(225,6,0,0.5)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 0 16px rgba(225,6,0,0.3)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                }}
              >
                <UserPlus size={15} />
                Add Athlete
              </button>
            )}
            <a href="/assessment-entry" className="btn-primary">+ New Assessment</a>
          </div>
        </div>

        {roster.length === 0 ? (
          <div
            className="rounded-2xl p-12 text-center"
            style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(225,6,0,0.1)', border: '1px solid rgba(225,6,0,0.2)' }}
            >
              <Users size={28} style={{ color: '#E10600' }} />
            </div>
            <h3 className="text-lg font-semibold text-zinc-200 mb-2">No athletes added yet.</h3>
            <p className="text-sm text-zinc-500 mb-6 max-w-sm mx-auto">
              Start building your roster by adding your first athlete.
            </p>
            {canAddAthlete ? (
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #E10600, #8B0000)',
                  color: '#ffffff',
                  boxShadow: '0 0 16px rgba(225,6,0,0.3)',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 0 24px rgba(225,6,0,0.5)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 0 16px rgba(225,6,0,0.3)';
                }}
              >
                <UserPlus size={15} />
                Add Athlete
              </button>
            ) : (
              <a href="/assessment-entry" className="btn-primary inline-flex items-center gap-2">
                <UserPlus size={15} />
                Add Your First Athlete
              </a>
            )}
          </div>
        ) : (
          <>
            <CoachKpiCards
              avgEps={avgEps}
              belowThreshold={belowThreshold}
              mostImproved={mostImproved}
              sessionsThisMonth={sessionsThisMonth}
              totalAthletes={roster.length}
            />

            {/* Daily Wellness Panel */}
            <div className="card p-5">
              <CoachWellnessPanel />
            </div>

            <AthleteRosterTable
              roster={roster}
              canAddAthlete={canAddAthlete}
              onAddAthlete={() => setShowAddModal(true)}
              coachId={user?.id}
            />
          </>
        )}
      </div>

      {/* Add Athlete Modal */}
      {showAddModal && user && (
        <AddAthleteModal
          coachId={user.id}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAthleteAdded}
        />
      )}
    </AppLayout>
  );
}